import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart, getCartLineId } from '../context/CartContext';
import { useLocale } from '../context/LocaleContext';
import { useToast } from '../context/ToastContext';
import { createOrder } from '../lib/orders';
import { calculateShipping, fetchPublicStoreSettings } from '../lib/storeSettings';
import './Checkout.css';

const STEPS = [
  { id: 'information', label: 'Information' },
  { id: 'delivery', label: 'Delivery' },
  { id: 'pay', label: 'Pay' },
];

const emptyForm = {
  name: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  zip: '',
  country: '',
};

function Checkout() {
  const { user } = useAuth();
  const { cartItems, totalPrice, clearCart } = useCart();
  const { formatMoney } = useLocale();
  const { pushToast } = useToast();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [consent, setConsent] = useState(false);
  const [shippingSettings, setShippingSettings] = useState(null);
  const [deliveryMethod, setDeliveryMethod] = useState('standard');
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (user?.email) {
      setForm(current => ({ ...current, email: user.email }));
    }
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    fetchPublicStoreSettings()
      .then(settings => {
        if (!cancelled) setShippingSettings(settings);
      })
      .catch(() => {
        if (!cancelled) {
          setShippingSettings({ shipping_flat_rate: 0, shipping_free_threshold: null });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const baseShipping = useMemo(
    () => calculateShipping(totalPrice, shippingSettings),
    [totalPrice, shippingSettings]
  );

  const shippingCost = deliveryMethod === 'express' ? baseShipping + 15 : baseShipping;
  const orderTotal = totalPrice + shippingCost;

  const handleChange = event => {
    const { name, value } = event.target;
    setForm(current => ({ ...current, [name]: value }));
  };

  const validateInformation = () => {
    if (!form.name.trim() || !form.email.trim()) {
      setError('Name and email are required.');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setError('Enter a valid email address.');
      return false;
    }
    setError('');
    return true;
  };

  const validateDelivery = () => {
    if (!form.address.trim() || !form.city.trim() || !form.zip.trim()) {
      setError('Address, city, and postcode are required.');
      return false;
    }
    setError('');
    return true;
  };

  const goNext = () => {
    if (step === 0 && !validateInformation()) return;
    if (step === 1 && !validateDelivery()) return;
    setStep(current => Math.min(current + 1, STEPS.length - 1));
  };

  const goBack = () => {
    setError('');
    setStep(current => Math.max(current - 1, 0));
  };

  const handlePlaceOrder = async () => {
    if (!consent) {
      setError('Please accept the Terms and Privacy policy to continue.');
      return;
    }
    if (!validateInformation() || !validateDelivery()) {
      setStep(0);
      return;
    }

    setError('');
    setSubmitting(true);

    try {
      const orderId = await createOrder({
        userId: user.id,
        cartItems,
        totalPrice: orderTotal,
        shipping: {
          name: form.name,
          email: form.email,
          address: form.address,
          city: form.city,
          zip: form.zip,
        },
      });

      clearCart();
      pushToast('Order placed. Thank you.');
      navigate(`/orders?placed=${orderId}`, { replace: true });
    } catch (err) {
      setError(err?.message || 'Could not place your order. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="checkout-page">
        <div className="checkout-empty">
          <p className="store-eyebrow">Checkout</p>
          <h1 className="store-title">Nothing to checkout</h1>
          <p className="store-lede">Your bag is empty.</p>
          <Link to="/shop" className="store-btn">
            Browse the shop
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <header className="checkout-header">
        <p className="store-eyebrow">Checkout</p>
        <h1 className="store-title">{STEPS[step].label}</h1>
        <p className="store-lede">
          {step === 0 && 'Who is this order for?'}
          {step === 1 && 'Where should we send it, and how fast?'}
          {step === 2 && 'Review and confirm. Card payment connects next.'}
        </p>
      </header>

      <ol className="checkout-steps" aria-label="Checkout progress">
        {STEPS.map((item, index) => (
          <li
            key={item.id}
            className={`checkout-step${index === step ? ' active' : ''}${
              index < step ? ' done' : ''
            }`}
          >
            <button
              type="button"
              disabled={index > step}
              onClick={() => {
                if (index < step) {
                  setError('');
                  setStep(index);
                }
              }}
            >
              <span className="checkout-step-index">{index + 1}</span>
              {item.label}
            </button>
          </li>
        ))}
      </ol>

      <div className="checkout-grid">
        <div className="checkout-form">
          {step === 0 && (
            <>
              <p className="checkout-form-eyebrow">Contact</p>
              <label className="store-field">
                Full name
                <input
                  className="store-input"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  autoComplete="name"
                />
              </label>
              <label className="store-field">
                Email
                <input
                  className="store-input"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  autoComplete="email"
                />
              </label>
              <label className="store-field">
                Phone <span className="checkout-optional">(optional)</span>
                <input
                  className="store-input"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange}
                  autoComplete="tel"
                />
              </label>
            </>
          )}

          {step === 1 && (
            <>
              <p className="checkout-form-eyebrow">Address</p>
              <label className="store-field">
                Address
                <input
                  className="store-input"
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  required
                  autoComplete="street-address"
                />
              </label>
              <label className="store-field">
                City
                <input
                  className="store-input"
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                  required
                  autoComplete="address-level2"
                />
              </label>
              <label className="store-field">
                ZIP / Postcode
                <input
                  className="store-input"
                  name="zip"
                  value={form.zip}
                  onChange={handleChange}
                  required
                  autoComplete="postal-code"
                />
              </label>
              <label className="store-field">
                Country <span className="checkout-optional">(optional)</span>
                <input
                  className="store-input"
                  name="country"
                  value={form.country}
                  onChange={handleChange}
                  autoComplete="country-name"
                />
              </label>

              <p className="checkout-form-eyebrow" style={{ marginTop: 12 }}>
                Delivery method
              </p>
              <div className="checkout-methods" role="radiogroup" aria-label="Delivery method">
                <label className={`checkout-method${deliveryMethod === 'standard' ? ' active' : ''}`}>
                  <input
                    type="radio"
                    name="deliveryMethod"
                    value="standard"
                    checked={deliveryMethod === 'standard'}
                    onChange={() => setDeliveryMethod('standard')}
                  />
                  <span>
                    <strong>Standard</strong>
                    <em>
                      {shippingSettings
                        ? baseShipping === 0
                          ? 'Free'
                          : formatMoney(baseShipping)
                        : '…'}
                    </em>
                  </span>
                </label>
                <label className={`checkout-method${deliveryMethod === 'express' ? ' active' : ''}`}>
                  <input
                    type="radio"
                    name="deliveryMethod"
                    value="express"
                    checked={deliveryMethod === 'express'}
                    onChange={() => setDeliveryMethod('express')}
                  />
                  <span>
                    <strong>Express</strong>
                    <em>
                      {shippingSettings
                        ? formatMoney(baseShipping + 15)
                        : '…'}
                    </em>
                  </span>
                </label>
              </div>
              <p className="checkout-method-note">
                Express is a designed option — carrier APIs will replace the flat uplift later.
              </p>
            </>
          )}

          {step === 2 && (
            <>
              <p className="checkout-form-eyebrow">Review</p>
              <div className="checkout-review">
                <div>
                  <p className="checkout-review-label">Contact</p>
                  <p>{form.name}</p>
                  <p>{form.email}</p>
                  {form.phone && <p>{form.phone}</p>}
                  <button type="button" className="store-link" onClick={() => setStep(0)}>
                    Edit
                  </button>
                </div>
                <div>
                  <p className="checkout-review-label">Ship to</p>
                  <p>{form.address}</p>
                  <p>
                    {form.city} {form.zip}
                  </p>
                  {form.country && <p>{form.country}</p>}
                  <p className="checkout-review-meta">
                    {deliveryMethod === 'express' ? 'Express' : 'Standard'} delivery
                  </p>
                  <button type="button" className="store-link" onClick={() => setStep(1)}>
                    Edit
                  </button>
                </div>
              </div>

              <div className="checkout-pay-panel">
                <p className="checkout-pay-title">Payment</p>
                <p className="checkout-pay-copy">
                  Card payment is ready to connect (Stripe or similar). Until then, placing an
                  order confirms the bag and reduces stock — no card is charged in this build.
                </p>
                <div className="checkout-pay-stub" aria-hidden="true">
                  <span>Card number</span>
                  <span>MM / YY</span>
                  <span>CVC</span>
                </div>
              </div>

              <label className="checkout-consent-check">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={event => setConsent(event.target.checked)}
                />
                <span>
                  I agree to the <Link to="/terms">Terms</Link> and{' '}
                  <Link to="/privacy">Privacy</Link> policy.
                </span>
              </label>
            </>
          )}

          {error && <p className="store-error">{error}</p>}

          <div className="checkout-nav">
            {step > 0 ? (
              <button type="button" className="store-btn store-btn--ghost" onClick={goBack}>
                Back
              </button>
            ) : (
              <Link to="/cart" className="store-link">
                Return to bag
              </Link>
            )}

            {step < STEPS.length - 1 ? (
              <button type="button" className="store-btn" onClick={goNext}>
                Continue
              </button>
            ) : (
              <button
                type="button"
                className="store-btn"
                onClick={handlePlaceOrder}
                disabled={submitting}
              >
                {submitting ? 'Placing order…' : `Place order — ${formatMoney(orderTotal)}`}
              </button>
            )}
          </div>
        </div>

        <aside className="checkout-summary" aria-label="Order summary">
          <p className="checkout-summary-eyebrow">Order summary</p>
          <div className="checkout-summary-list">
            {cartItems.map(item => {
              const lineId = item.cartLineId || getCartLineId(item.id, item.size);
              return (
                <div key={lineId} className="checkout-summary-line">
                  <span>
                    {item.name} × {item.quantity}
                    {(item.size || item.variant) && (
                      <span className="checkout-summary-meta">
                        {[item.size && `Size ${item.size}`, item.variant]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    )}
                  </span>
                  <span>{formatMoney(Number(item.price) * item.quantity)}</span>
                </div>
              );
            })}
          </div>
          <div className="checkout-summary-rows">
            <div className="checkout-summary-line">
              <span>Subtotal</span>
              <span>{formatMoney(totalPrice)}</span>
            </div>
            <div className="checkout-summary-line">
              <span>Shipping</span>
              <span>
                {shippingSettings
                  ? shippingCost === 0
                    ? 'Free'
                    : formatMoney(shippingCost)
                  : '…'}
              </span>
            </div>
          </div>
          <div className="checkout-summary-total">
            <span>Total</span>
            <span>{formatMoney(orderTotal)}</span>
          </div>
        </aside>
      </div>
    </div>
  );
}

export default Checkout;
